import { useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { getDesignDetail } from '../redux/slices/DesignSlice';
import { setDimensions } from '../redux/slices/SizeSlice';
import { updateName, updateNum } from '../redux/slices/PlanDetailSlice';
import { useFontLoader } from './useFontLoader';

/**
 * Custom hook for handling design data and setting it to polotno store
 * 
 * @param {Object} store - Polotno store instance
 * @param {Function} setStoreData - Function to set store data (from polotno store)
 * @returns {Object} Hook state and functions
 * 
 * @example
 * ```jsx
 * import useDesignData from '../hook/useDesignData';
 * 
 * const MyComponent = ({ store }) => {
 *   const setStoreData = async (data) => {
 *     store.loadJSON(data);
 *     await store.waitLoading();
 *   };
 * 
 *   const {
 *     designInfo,
 *     isDesignLoading,
 *     hasDesignData,
 *     textFieldsNum,
 *     textFieldsName,
 *     loadDesignDetail,
 *     setDesignDataToStore,
 *     resetDesignData
 *   } = useDesignData(store, setStoreData);
 * 
 *   return (
 *     <div>
 *       {isDesignLoading && <p>Loading design...</p>}
 *       {hasDesignData && <p>Design loaded: {designInfo.design_name}</p>}
 *     </div>
 *   );
 * };
 * ```
 */
const useDesignData = (store, setStoreData) => {
    const dispatch = useDispatch();
    const { id } = useParams();
    const processedDesignId = useRef(null);
    const setStoreDataRef = useRef(setStoreData);
    const { loading: fontsLoading, fontsLoaded, teedexFontsList, ownFontsList } = useFontLoader();

    // Update ref when setStoreData changes
    useEffect(() => {
        if(setStoreData){
            setStoreDataRef.current = setStoreData;
        }
    }, [setStoreData]);

    // Redux selectors
    const { designInfo, isLoading: designLoading } = useSelector(state => state.design);
    const { textFieldsNum, textFieldsName } = useSelector(state => state?.userPlan);
    const { sizes, isSizeFetched } = useSelector(state => state.sizeDimension);

    /**
     * Load design detail by ID
     * @param {string} designId - The design ID to load
     */
    const loadDesignDetail = useCallback((designId) => {
        if (designId) {
            dispatch(getDesignDetail(designId));
        }
    }, [dispatch]);

    /**
     * Generate default canvases from sizes when no store data is available
     * @param {Object} store - Polotno store instance
     * @param {Object} sizes - Available sizes data
     * @param {string} selectedType - Selected size type
     */
    const generateDefaultCanvases = useCallback((store, sizes, selectedType = 'back') => {
        if (!store || !sizes || sizes.length === 0) return;

        try {
            store.clear();
            const dpi = 72;

            // Generate dimensions from sizes
            const generateSizeDimensions = (sizes, type) => {
                const calculateDimensions = (inchWidth, inchHeight) => ({
                    width: Math.round(parseFloat(inchWidth) * 72),
                    height: Math.round(parseFloat(inchHeight) * 72),
                    inchWidth,
                    inchHeight,
                    inch: `${inchWidth} X ${inchHeight} in`,
                });

                const dimensions = {};
                sizes.forEach((size) => {
                    let age = size.age;
                    // Convert AGE/Age to Yr
                    age = age.replace(/AGE|Age/g, "Yr");
                    age = age.replace(/ /g, "_");
                    age = age.replace(/_TO_/g, "-");

                    if (type === "back") {
                        dimensions[age] = {
                            ...calculateDimensions(size.width, size.height),
                            size: age,
                            sizeNumber: size.label,
                            sizeLabel: size.age,
                        };
                    } else if (type === "halfSleeves") {
                        dimensions[age] = {
                            ...calculateDimensions(size.halfSleeveWidth, size.halfSleeveHeight),
                            size: age,
                            sizeNumber: size.label,
                            sizeLabel: size.age,
                        };
                    } else if (type === "fullSleeves") {
                        dimensions[age] = {
                            ...calculateDimensions(size.fullSleeveWidth, size.fullSleeveHeight),
                            size: age,
                            sizeNumber: size.label,
                            sizeLabel: size.age,
                        };
                    }
                });
                return dimensions;
            };

            const sizeDimensions = generateSizeDimensions(sizes, selectedType);

            // Find a medium size for master page (or use the first available size)
            const mediumSize = sizeDimensions['M'] || sizeDimensions[Object.keys(sizeDimensions)[0]] || {};
            
            if (mediumSize.width && mediumSize.height) {
                // Create master page
                store.addPage({
                    name: "master",
                    width: mediumSize.width,
                    height: mediumSize.height,
                    id: "master",
                    background: "rgba(0,0,0,0)",
                    custom: {
                        size: "M",
                        inch: mediumSize.inch,
                        pixels: `${mediumSize.width} X ${mediumSize.height} px`,
                    },
                });

                // Create pages for all available sizes
                Object.keys(sizeDimensions).forEach((sizeKey) => {
                    const item = sizeDimensions[sizeKey];
                    if (item.width && item.height) {
                        store.addPage({
                            name: sizeKey.split("_").join(" "),
                            width: item.width,
                            height: item.height,
                            id: sizeKey,
                            background: "rgba(0,0,0,0)",
                            custom: {
                                size: sizeKey,
                                inch: item.inch,
                                pixels: `${item.width} X ${item.height} px`,
                            },
                        });
                    }
                });

                store.selectPage("master");
            }
        } catch (error) {
            console.error('Error generating default canvases:', error);
        }
    }, []);

    /**
     * Set design data to polotno store and update Redux state
     * @param {Object} designData - The design data object
     */
    const setDesignDataToStore = useCallback(async (designData) => {
        try {
            if (designData) {
                // Update dimensions in Redux
                if (designData?.store_data?.dimensions) {
                    dispatch(setDimensions({
                        dimensions: designData.store_data.dimensions,
                        selectedType: designData.store_data.selected_type,
                    }));
                }

                // Update text field states
                if (designData?.store_data?.is_player_name_checked !== undefined) {
                    dispatch(updateName(designData.store_data.is_player_name_checked));
                }

                if (designData?.store_data?.is_player_number_checked !== undefined) {
                    dispatch(updateNum(designData.store_data.is_player_number_checked));
                }
            }

            if (!store) {
                return;
            }

            // Check if we have store data, if not generate default canvases
            if (designData?.store_data?.store_json && setStoreData) {
                // Load existing store data
                await setStoreDataRef.current(designData.store_data.store_json);
            } else if (!designData?.store_data || !designData?.store_data?.store_json) {
                // No store data available, generate default canvases from sizes API
                console.log('No store data found, generating default canvases from sizes API');
                if (sizes && sizes.length > 0 && isSizeFetched) {
                    // Try to get selectedType from design data, localStorage, or default to 'back'
                    const selectedType = designData?.store_data?.selected_type || 
                                       (typeof localStorage !== 'undefined' ? localStorage.getItem("selectedType") : null) || 
                                       'back';
                    generateDefaultCanvases(store, sizes, selectedType);
                    
                    // Set dimensions in Redux for consistency
                    if (sizes.length > 0) {
                        dispatch(setDimensions({
                            selectedType: selectedType,
                        }));
                    }
                } else {
                    console.log('Sizes not loaded yet, will retry when sizes are available');
                }
            }
        } catch (error) {
            console.error('Error setting design data to store:', error);
        }
    }, [store, dispatch, generateDefaultCanvases, sizes, isSizeFetched]);

    /**
     * Initialize design data when component mounts
     */
    useEffect(() => {
        if (id && fontsLoaded) {
            loadDesignDetail(id);
        }
    }, [id, loadDesignDetail, fontsLoaded]);

    /**
     * Set design data to store when designInfo changes
     */
    useEffect(() => {
        if (id && designInfo && processedDesignId.current !== designInfo.id) {
            processedDesignId.current = designInfo.id;
            setDesignDataToStore(designInfo);
        }
    }, [id, designInfo, setDesignDataToStore]);

    /**
     * Handle case when sizes become available after designInfo is loaded but has no store data
     */
    useEffect(() => {
        if (designInfo && 
            (!designInfo?.store_data || !designInfo?.store_data?.store_json) &&
            sizes && sizes.length > 0 && isSizeFetched &&
            store && store.pages.length === 0) {
            console.log('Sizes loaded after designInfo, generating default canvases');
            setDesignDataToStore(designInfo);
        }
    }, [sizes, isSizeFetched, designInfo, setDesignDataToStore, store]);

    /**
     * Reset design data (clear store and Redux state)
     */
    const resetDesignData = useCallback(() => {
        if (store) {
            store.clear();
        }
        dispatch(updateName(false));
        dispatch(updateNum(false));
    }, [store, dispatch]);

    /**
     * Check if design is currently loading
     */
    const isDesignLoading = designLoading;

    /**
     * Check if design data is available
     */
    const hasDesignData = !!designInfo;

    return {
        // State
        designInfo,
        isDesignLoading,
        hasDesignData,
        textFieldsNum,
        textFieldsName,

        // Functions
        loadDesignDetail,
        setDesignDataToStore,
        resetDesignData,
        generateDefaultCanvases,
    };
};

export default useDesignData; 